import { useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Shield, Sparkles, AlertTriangle, ArrowRight } from "lucide-react";
import { toast } from "sonner";

export default function Protect() {
  const [situation, setSituation] = useState("");
  const [country, setCountry] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const ask = async () => {
    if (!situation.trim()) return;
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post("/protect/rights", { situation, country: country || null });
      setResult(data);
    } catch { toast.error("Guidance unavailable"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-10" data-testid="protect-page">
      <div>
        <div className="label-caps mb-2 flex items-center gap-1.5"><Shield className="h-3 w-3 text-rose-400" />Pillar · Protect</div>
        <h1 className="text-3xl sm:text-5xl tracking-tight font-semibold">
          Protect the dignity<br />of every worker.
        </h1>
        <p className="text-muted-foreground mt-3 max-w-2xl">
          Clarify rights, protections and escalation routes. Grounded in principles of global employment practice.
          Not legal advice — a starting point for informed action.
        </p>
      </div>

      <Card className="p-6 md:p-8">
        <div className="label-caps mb-2 flex items-center gap-1.5"><Sparkles className="h-3 w-3 text-primary" />Rights Clarifier</div>
        <h2 className="text-xl md:text-2xl font-medium">Describe the situation</h2>
        <div className="mt-4 grid md:grid-cols-6 gap-4">
          <div className="md:col-span-4">
            <Label>What happened?</Label>
            <Textarea rows={5} value={situation} onChange={(e) => setSituation(e.target.value)}
              placeholder="e.g. I was fired a week after reporting that my manager was manipulating customer data."
              className="mt-1.5" data-testid="protect-situation" />
          </div>
          <div className="md:col-span-2">
            <Label>Country (optional)</Label>
            <Input value={country} onChange={(e) => setCountry(e.target.value)}
              placeholder="e.g. United Kingdom" className="mt-1.5" data-testid="protect-country" />
            <Button onClick={ask} disabled={busy || !situation.trim()} className="w-full rounded-full mt-4" data-testid="protect-ask">
              <Shield className="h-4 w-4 mr-1" />{busy ? "Checking…" : "Clarify my rights"}
            </Button>
          </div>
        </div>
      </Card>

      {result && (
        <Card className="p-6 md:p-8" data-testid="protect-result">
          <div className="label-caps mb-3">Guidance</div>
          <pre className="whitespace-pre-wrap text-sm font-sans leading-relaxed">{result.answer}</pre>
          {result.citations?.length > 0 && (
            <div className="mt-6 pt-5 border-t border-border">
              <div className="label-caps mb-2">Grounded in</div>
              <div className="space-y-1">
                {result.citations.map((c) => (
                  <Link key={c.n} to={`/app/learn/chapter/${c.chapter_id}`}
                    className="block text-xs font-mono text-muted-foreground hover:text-foreground">
                    [{c.n}] {c.title} <ArrowRight className="inline h-3 w-3 ml-1" />
                  </Link>
                ))}
              </div>
            </div>
          )}
          <div className="mt-6 flex items-start gap-2 text-xs text-muted-foreground">
            <AlertTriangle className="h-3.5 w-3.5 text-amber-400 shrink-0 mt-0.5" />
            <span>This is informational guidance grounded in the Mahakali Tribunal library. Laws vary by jurisdiction. For
            consequential action, consult a qualified employment lawyer in your country.</span>
          </div>
        </Card>
      )}

      <Card className="p-6 md:p-8 border-rose-400/30">
        <div className="flex items-start gap-4">
          <Shield className="h-6 w-6 text-rose-400 shrink-0 mt-1" />
          <div>
            <div className="label-caps mb-1">The sixth pillar</div>
            <h3 className="text-xl font-medium">Dignity is the floor, not the ceiling.</h3>
            <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
              Every other pillar — Discover, Learn, Practice, Earn, Advance — assumes workers are protected.
              Pay promised. Voice heard. Safety maintained. Difference respected. Dissent protected. Exit fair.
              This is the standard the tribunal holds, universally.
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
}
